import React, { useId, useLayoutEffect, useRef, useState } from "react";
import { mapStateManager } from "../MapStateManager";

const Sections = React.memo(
  ({
    data,
    isDisabled,
    activeMapAction,
    displayInventoryInfo,
    getSectionState,
    handleSectionSelect,
    deriveInventoryInfoFrom,
    showTooltip,
    hideTooltip,
  }) => {
    const [hoveredSectionId, setHoveredSectionId] = useState(null);

    const handleSectionAction = (e, sectionId, hovering) => {
      // if section is disabled don't change hover of section
      if (isDisabled) return;
      handleTooltip(e, sectionId, hovering);
      applyFilterToSection(sectionId, hovering);
    };

    const handleTooltip = (e, sectionId, show) => {
      if (show) {
        const section = data.sections[sectionId];
        const sectionDetails = {
          type: "section",
          sectionNumber: section?.sectionNumber,
        };
        showTooltip(sectionDetails, { evt: e });
      } else {
        hideTooltip();
      }
    };

    const applyFilterToSection = (sectionId, hovering) => {
      const section = data.sections[sectionId];
      if (!section) return;

      // For non-zoomable sections, just update the section
      if (section.rows.length === 0) {
        mapStateManager.setState(sectionId, { selected: hovering });
        return;
      }

      setHoveredSectionId(hovering ? sectionId : null);
    };

    return (
      <g className="polygons">
        {data?.sections &&
          Object.values(data.sections).map((section) => {
            return section?.zoomable ? (
              <path
                key={section.sectionId}
                d={section.path}
                id={section.sectionId}
                data-section-root={activeMapAction === 1 ? "true" : undefined}
                onClick={() => handleSectionSelect(section)}
                onMouseOver={(e) =>
                  handleSectionAction(e, section.sectionId, true)
                }
                onMouseOut={(e) =>
                  handleSectionAction(e, section.sectionId, false)
                }
                pointerEvents={activeMapAction === 1 ? "auto" : "none"}
                opacity={hoveredSectionId === section.sectionId ? 0.2 : 0}
                fill="#3E8BF7"
                style={{ cursor: activeMapAction === 1 ? "pointer" : "grab" }}
              />
            ) : (
              <GASection
                key={section?.sectionId}
                section={section}
                handleSectionSelect={handleSectionSelect}
                displayInventoryInfo={displayInventoryInfo}
                getSectionState={getSectionState}
                deriveInventoryInfoFrom={deriveInventoryInfoFrom}
                isDisabled={isDisabled}
              />
            );
          })}
      </g>
    );
  },
);

function GASection({
  section,
  handleSectionSelect,
  displayInventoryInfo,
  getSectionState,
  deriveInventoryInfoFrom,
  isDisabled,
}) {
  const reactId = useId();
  const pathRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const [sectionInventoryDetails, setSectionInventoryDetails] = useState(null);
  const sectionStatus = getSectionState(section) || {
    disabled: false,
    color: section?.fill,
  };
  const gradientId = `admin-seatmap-gradient-${reactId.replace(/:/g, "")}`;

  // Compute section inventory details after mount and when dependencies change
  useLayoutEffect(() => {
    if (!pathRef.current || !section) return;
    if (!displayInventoryInfo) {
      setSectionInventoryDetails(null);
      return;
    }

    const selfRect = pathRef.current.getBBox();
    const sectionId = section.sectionId;
    const totalSpots = section.spots?.length ?? 0;
    if (totalSpots <= 0) {
      setSectionInventoryDetails(null);
      return;
    }

    // Collect totals per inventory item, including categories
    const inventoryTotals = [];
    (deriveInventoryInfoFrom || []).forEach((item) => {
      const inventory = item.inventory || {};
      Object.values(inventory).forEach((actualInventory) => {
        const color = actualInventory.color;
        let totalCount = 0;

        // Add main spots for this inventory item
        if (actualInventory.gaSeats && actualInventory.gaSeats[sectionId]) {
          totalCount += actualInventory.gaSeats[sectionId].length;
        }

        // Add spots from categories
        if (actualInventory.categories) {
          Object.values(actualInventory.categories).forEach((category) => {
            if (category.gaSeats && category.gaSeats[sectionId]) {
              totalCount += category.gaSeats[sectionId].length;
            }
          });
        }

        // Store the total count and color for this inventory item
        inventoryTotals.push({ totalCount, color });
      });
    });

    // Calculate total assigned spots
    const totalAssigned = inventoryTotals.reduce(
      (sum, { totalCount }) => sum + totalCount,
      0,
    );
    const unassigned = totalSpots - totalAssigned;

    // Compute color stops for the gradient
    const colorStops = [];
    let cumsum = 0;

    // Add segment for unassigned spots, if any
    if (unassigned > 0) {
      const proportion = unassigned / totalSpots;
      const start = cumsum;
      const end = cumsum + proportion;
      colorStops.push(start, "gray");
      colorStops.push(Math.min(end, 1), "gray");
      cumsum = end;
    }

    // Add segments for each inventory item
    inventoryTotals.forEach(({ totalCount, color }) => {
      if (totalCount > 0) {
        const proportion = totalCount / totalSpots;
        const start = cumsum;
        const end = cumsum + proportion;
        colorStops.push(start, color);
        colorStops.push(Math.min(end, 1), color);
        cumsum = end;
      }
    });

    // Set gradient properties
    setSectionInventoryDetails({
      startPoint: {
        x: selfRect.x + selfRect.width / 2,
        y: selfRect.y,
      },
      endPoint: {
        x: selfRect.x + selfRect.width / 2,
        y: selfRect.y + selfRect.height,
      },
      colorStops,
    });
  }, [deriveInventoryInfoFrom, displayInventoryInfo, section]);

  const fill =
    displayInventoryInfo && sectionInventoryDetails
      ? `url(#${gradientId})`
      : sectionStatus.color;
  const isInteractive = !isDisabled && !sectionStatus.disabled;

  return (
    <g
      data-section-root={isInteractive ? "true" : undefined}
      onClick={() => handleSectionSelect(section)}
      pointerEvents={isInteractive ? "auto" : "none"}
      onMouseOver={() => setHovered(true)}
      onMouseOut={() => setHovered(false)}
      style={{ cursor: isInteractive ? "pointer" : "default" }}
    >
      {sectionInventoryDetails && (
        <defs>
          <linearGradient
            id={gradientId}
            gradientUnits="userSpaceOnUse"
            x1={sectionInventoryDetails.startPoint.x}
            y1={sectionInventoryDetails.startPoint.y}
            x2={sectionInventoryDetails.endPoint.x}
            y2={sectionInventoryDetails.endPoint.y}
          >
            {sectionInventoryDetails.colorStops
              .reduce((stops, value, index, values) => {
                if (index % 2 !== 0) return stops;
                return [...stops, { offset: value, color: values[index + 1] }];
              }, [])
              .map((stop, index) => (
                <stop
                  key={`${stop.offset}-${stop.color}-${index}`}
                  offset={`${Math.max(0, Math.min(1, stop.offset)) * 100}%`}
                  stopColor={stop.color}
                />
              ))}
          </linearGradient>
        </defs>
      )}
      {/* Section */}
      <path
        ref={pathRef}
        fill={fill}
        d={section.path}
        className={`path`}
        id={section.sectionId}
        stroke={section?.stroke}
        strokeWidth={Number(section?.strokeWidth) || 0}
      />
      {hovered && (
        <path
          d={section.path}
          fill="rgba(255, 255, 255, 0.2)" // Subtle hover effect
          pointerEvents="none"
        />
      )}

      {/* Visuals, probably text */}
      <path
        d={section?.identifier?.path}
        fill={section?.identifier?.fill}
        pointerEvents="none"
      />
    </g>
  );
}

export default Sections;
